<template>
  <div class="min-h-screen">
    <Head title="Crear cobranza" />
    
    <!-- Header Section -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center space-x-4">
          <button 
            class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
            @click="back()"
          >
            <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Volver
          </button>
          <h1 class="text-2xl font-bold text-gray-900">Crear cobranza</h1>
        </div>
      </div>

      <!-- Client Selection Section -->
      <div class="bg-white shadow-sm rounded-lg overflow-hidden mb-6">
        <div class="p-6">
          <h2 class="text-lg font-medium text-gray-900 mb-4">Seleccionar Propietario</h2>
          <div class="space-y-4">
            <div v-if="!client" class="w-full">
              <text-input 
                v-model="form.search.client" 
                label="Buscar propietario"
                placeholder="Ingrese el nombre o identificación del propietario"
                class="w-full"
              />
              <div v-if="clients && clients.length > 0" class="mt-2 space-y-2">
                <div 
                  v-for="client in clients" 
                  :key="client.id" 
                  class="p-3 bg-gray-50 hover:bg-gray-100 rounded-md cursor-pointer transition-colors duration-150"
                  @click="addClient(client)"
                >
                  <div class="flex justify-between items-center">
                    <div>
                      <p class="text-sm font-medium text-gray-900">{{ client.name }}</p>
                      <p class="text-sm text-gray-500">{{ client.identification }}</p>
                    </div>
                    <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div class="flex justify-between items-start">
                <div>
                  <h3 class="text-sm font-medium text-blue-900">Propietario seleccionado</h3>
                  <p class="mt-1 text-sm text-blue-700">{{ client.name }}</p>
                  <p class="text-sm text-blue-600">{{ client.identification }}</p>
                </div>
                <button 
                  @click="client = null" 
                  class="text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  Cambiar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Products Section -->
      <div class="bg-white shadow-sm rounded-lg overflow-hidden mb-6">
        <div class="p-6">
          <div class="flex items-center justify-between mb-6">
            <button 
              @click="addQuickProduct()" 
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
            >
              <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Agregar servicio
            </button>
            <div class="w-64">
              <text-input 
                v-model="form.search.terms" 
                placeholder="Buscar productos..."
                class="w-full"
              />
            </div>
          </div>

          <!-- Products List -->
          <div v-if="listProducts && products.data.length > 0" class="mb-6">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disponibles</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr 
                    v-for="product in products.data" 
                    :key="product.id" 
                    class="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                    @click="selectProduct(product)"
                  >
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ product.code }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ product.name }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ product.quantity }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${{ product.prices[0].price }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Selected Products -->
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cuota / Servicio</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th scope="col" class="relative px-6 py-3">
                    <span class="sr-only">Acciones</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(product, key) in form.products" :key="product.id" class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <text-input 
                      v-if="product.quick" 
                      v-model="form.products[key].name"
                      class="w-full"
                    />
                    <span v-else class="text-sm text-gray-900">{{ product.name }}</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <text-input 
                      v-model="form.products[key].quantity" 
                      type="number"
                      :max="product.stock"
                      class="w-24"
                    />
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <text-input 
                      v-model="form.products[key].price"
                      type="number"
                      step="0.01"
                      class="w-32"
                    />
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${{ (product.price * product.quantity).toFixed(2) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      @click="removeProduct(product)"
                      class="text-red-600 hover:text-red-900"
                    >
                      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot class="bg-gray-50">
                <tr>
                  <td colspan="3" class="px-6 py-4 text-right text-sm font-medium text-gray-500">Total</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${{ getTotal() }}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colspan="3" class="px-6 py-4 text-right text-sm font-medium text-gray-500">Total BsD.</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">BsD. {{ (getTotal() * form.change).toFixed(2) }}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <!-- Additional Information Section -->
      <div class="bg-white shadow-sm rounded-lg overflow-hidden mb-6">
        <div class="p-6">
          <h2 class="text-lg font-medium text-gray-900 mb-4">Información Adicional</h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <text-input 
                v-model="form.number_order" 
                label="Correlativo"
                placeholder="Dejar en blanco para generación automática"
                class="w-full"
              />
            </div>
            <div>
              <text-input 
                v-model="form.comentary" 
                label="Observación"
                class="w-full"
              />
            </div>
            <div>
              <text-input 
                v-model="form.change" 
                label="Tasa de cambio"
                type="number"
                step="0.01"
                class="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="flex justify-end">
        <button 
          @click="generateOrder"
          class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
        >
          <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          Generar cobranza
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '../../Shared/Layout.vue'
import TextInput from '@/Shared/TextInput'
import pickBy from 'lodash/pickBy'
import { Head } from '@inertiajs/inertia-vue3'
import SelectInput from '@/Shared/SelectInput.vue'

export default {
  components: {
    SelectInput,
    TextInput,
    Head,
  },
  layout: Layout,
  props: {
    products: Object,
    brands: Object,
    parts: Object,
    models: Object,
    deposits: Object,
    regions: Object,
    change: Number,
    clients: Object
  },
  data() {
    return {
      client: null,
      form: this.$inertia.form({
        search: {
          terms: '',
          deposit: '',
          brand_id: '',
          part_id: '',
          truck_model_id: '',
          region_id: '',
          client_id: '',
          client: ''
        },
        isCredit: false,
        number_order: null,
        client_id: '',
        comentary: '',
        products: [],
        method: '',
        referencePay: '',
        change: this.change,
      }),
      listProducts: false,
      showClientCreate: false,
    }
  },
  watch: {
    'form.search.terms': function () {
      this.onSearchProductsChange()
    },
    'form.search.deposit': function () {
      this.onSearchProductsChange()
    },
    'form.search.brand_id': function () {
      this.onSearchProductsChange()
    },
    'form.search.part_id': function () {
      this.onSearchProductsChange()
    },
    'form.search.truck_model_id': function () {
      this.onSearchProductsChange()
    },
    'form.search.region_id': function () {
      this.onSearchProductsChange()
    },
    'form.search.client_id': function () {
      this.onSearchProductsChange()
    },
    'form.search.client': function() {
      this.onSearchProductsChange()
    }
  },
  methods: {
    getTotal() {
      let total = 0
      this.form.products.forEach((product) => {
        total += product.price * product.quantity
      })
      return total.toFixed(2)
    },
    addClient(client) {
      this.client = client
      this.form.client_id = client.id
    },
    back() {
      window.history.back()
    },
    addQuickProduct() {
      this.form.products.push({ 
        id: 'quick', 
        code: '', 
        name: '', 
        deposit: '', 
        quantity: 1, 
        price: 0, 
        quick: true 
      })
    },
    selectProduct(product) {
      if (product.quantity <= 0) {
        alert('No hay stock de este producto')
        return
      }

      const exists = this.form.products.some(prd => prd.id === product.id)
      if (!exists) {
        this.form.products.push({ 
          ...product, 
          stock: product.quantity, 
          quantity: 1, 
          price: product?.prices[0]?.price || 0, 
          quick: false 
        })
      }
    },
    removeProduct(product) {
      if (confirm('¿Estás seguro que quieres eliminar este item?')) {
        this.form.products = this.form.products.filter(prd => prd.id !== product.id)
      }
    },
    generateOrder() {
      if (!this.client) {
        alert('Debe seleccionar un propietario')
        return
      }
      if (this.form.products.length === 0) {
        alert('Debe agregar al menos un producto')
        return
      }
      this.form.post('/orders')
    },
    onSearchProductsChange() {
      this.$inertia.get('/orders/create', pickBy({ search: this.form.search }), {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    }
  },
}
</script>
